import { Route, NavigationGuardNext } from 'vue-router';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { FETCH_PARENT_REGISTRATION } from '@/graphql/account/queries';

const LEGACY_SEARCH_CARE_TYPES = ['PET_CARE', 'HOUSEKEEPING', 'COMPANION_CARE', 'SPECIAL_NEEDS'];
const DEFAULT_CARE_TYPE = 'babysitter';

export function buildLegacyProviderSearchUrl(searchStrategy: string, zipcode?: string): string {
  const query: Record<string, string> = {
    search_strategy: searchStrategy,
    careType: searchStrategy,
  };
  if (zipcode) query.zipcode = zipcode;
  return `/profile/provider/search?${new URLSearchParams(query).toString()}`;
}

function getCareTypeFromRouteQuery(to: Route): string {
  const raw = typeof to.query?.careType === 'string' ? to.query.careType : '';
  return raw ? raw.toLowerCase() : '';
}

function shouldBypassRegistrationCheck(to: Route, careType: string): boolean {
  const hasSearchStrategy = Boolean(to.query?.search_strategy);
  const hasExplicitNonDefaultCareType = Boolean(careType && careType !== 'both');
  const isFromJobPost = to.query?.source === 'job-post';
  return hasSearchStrategy || hasExplicitNonDefaultCareType || isFromJobPost;
}

function isLegacyRegistrationCareType(careType: string): boolean {
  return LEGACY_SEARCH_CARE_TYPES.includes(careType);
}

function redirectToLegacyProviderSearch(
  registrationCareType: string,
  zipcode: string | undefined,
  next: NavigationGuardNext
): void {
  const url = buildLegacyProviderSearchUrl(registrationCareType.toLowerCase(), zipcode ? String(zipcode) : undefined);
  window.location.replace(url);
  next(false);
}

async function fetchParentRegistrationCareType(): Promise<string | null> {
  try {
    const apolloClient = getSharedApolloClient();
    const { data } = await apolloClient.query({
      query: FETCH_PARENT_REGISTRATION,
      fetchPolicy: 'cache-first',
    });
    return data?.parentRegistration?.careType ?? null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking parent registration for search:', error);
    return null;
  }
}

function allowSearchWithNormalizedQuery(to: Route, careType: string, next: NavigationGuardNext): void {
  if (!careType) {
    next({
      path: to.path,
      query: { ...to.query, careType: DEFAULT_CARE_TYPE },
      replace: true,
    });
  } else {
    next();
  }
}

const searchCareTypeGuard = async (to: Route, _from: Route, next: NavigationGuardNext) => {
  const careType = getCareTypeFromRouteQuery(to);

  if (shouldBypassRegistrationCheck(to, careType)) {
    // Still normalize the URL with a careType if missing, so subsequent navigations
    // (e.g., when source param is removed) will bypass registration check
    allowSearchWithNormalizedQuery(to, careType, next);
    return;
  }

  const registrationCareType = await fetchParentRegistrationCareType();
  if (registrationCareType && isLegacyRegistrationCareType(registrationCareType)) {
    const zipcode = to.query?.zipcode ? String(to.query.zipcode) : undefined;
    redirectToLegacyProviderSearch(registrationCareType, zipcode, next);
    return;
  }

  allowSearchWithNormalizedQuery(to, careType, next);
};

export default searchCareTypeGuard;
